import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import {
  getApiBaseUrl,
  setStoredApiBaseUrl,
  DEFAULT_API_BASE_URL,
} from '../api/client';

const COLOR_MODE_KEY = 'evs_color_mode';

const SettingsContext = createContext(null);

function systemPrefersDark() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function SettingsProvider({ children }) {
  const [apiBaseUrl, setApiBaseUrlState] = useState(() => getApiBaseUrl());
  const [colorMode, setColorModeState] = useState(
    () => localStorage.getItem(COLOR_MODE_KEY) || 'system'
  );
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  const [authBanner, setAuthBanner] = useState(null);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event) => setSystemDark(event.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  const setApiBaseUrl = useCallback((url) => {
    setStoredApiBaseUrl(url);
    setApiBaseUrlState(url || DEFAULT_API_BASE_URL);
  }, []);

  const setColorMode = useCallback((mode) => {
    localStorage.setItem(COLOR_MODE_KEY, mode);
    setColorModeState(mode);
  }, []);

  const triggerAuthBanner = useCallback((message) => {
    setAuthBanner(
      message ||
        'Your session has expired. Sign in to the dashboard again to keep checking contacts.'
    );
  }, []);

  const clearAuthBanner = useCallback(() => setAuthBanner(null), []);

  const resolvedMode = colorMode === 'system' ? (systemDark ? 'dark' : 'light') : colorMode;

  const value = useMemo(
    () => ({
      apiBaseUrl,
      setApiBaseUrl,
      defaultApiBaseUrl: DEFAULT_API_BASE_URL,
      colorMode,
      setColorMode,
      resolvedMode,
      authBanner,
      triggerAuthBanner,
      clearAuthBanner,
    }),
    [apiBaseUrl, setApiBaseUrl, colorMode, setColorMode, resolvedMode, authBanner, triggerAuthBanner, clearAuthBanner]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
